import React, { useState, useEffect, useRef } from 'react';
import NavbarElement from './NavbarElement';
import NavbarMobileMenu from './NavbarMobileMenu';
import { Menu, X } from 'lucide';
import { v4 as uuidv4 } from 'uuid';
import { getNavbar } from '#api';
import { colors } from '#utils';
import { MorphIcon } from 'morphicons/react';

const Navbar = () => {
    const colorsKeys = Object.keys(colors);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isNavbarVisible, setIsNavbarVisible] = useState(true);
    const [animate, setAnimate] = useState(true);

    useEffect(() => {
        let timeoutId;
        const intervalId = setInterval(() => {
            setAnimate(false);
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                setAnimate(true);
            }, 50);
        }, 60 * 1000);

        return () => {
            clearInterval(intervalId);
            clearTimeout(timeoutId);
        };
    }, []);

    const lastScrollY = useRef(0);

    useEffect(() => {
        const loadData = async () => {
            try {
                const result = await getNavbar();

                setData(result.data);
            } catch (e) {
                console.log(e);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (mobileMenuOpen) {
                return;
            }
            const currentScrollY = window.scrollY;

            if (currentScrollY < lastScrollY.current) {
                setIsNavbarVisible(true);
            } else if (currentScrollY > 100) {
                setIsNavbarVisible(false);
            }

            lastScrollY.current = currentScrollY;
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [mobileMenuOpen]);

    useEffect(() => {
        if (mobileMenuOpen) {
            setIsNavbarVisible(true);
        }
    }, [mobileMenuOpen]);

    useEffect(() => {
        const previousDocumentOverflow = document.documentElement.style.overflow;
        const previousOverflow = document.body.style.overflow;

        if (mobileMenuOpen) {
            document.documentElement.style.overflow = 'hidden';
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.documentElement.style.overflow = previousDocumentOverflow;
            document.body.style.overflow = previousOverflow;
        };
    }, [mobileMenuOpen]);

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const handleMobileMenuItemClick = () => {
        setMobileMenuOpen(false);
    };

    if (loading)
        return (
            <React.Fragment>
                <nav
                    className={`anikino__navbar-flex ${mobileMenuOpen ? 'mobile-menu-open' : ''} ${
                        !isNavbarVisible ? 'navbar-hidden' : ''
                    }`}
                >
                    <NavbarElement page={{ name: 'logo' }} color={colorsKeys[1]} />
                    {Array(9)
                        .fill(0)
                        .map((_, index) => (
                            <NavbarElement
                                key={uuidv4()}
                                page={{ name: 'skeleton' }}
                                color={colorsKeys[index % 4]}
                            />
                        ))}
                </nav>
            </React.Fragment>
        );

    return (
        <React.Fragment>
            <nav
                className={`anikino__navbar-flex ${mobileMenuOpen ? 'mobile-menu-open' : ''} ${
                    !isNavbarVisible ? 'navbar-hidden' : ''
                }`}
            >
                <NavbarElement page={{ name: 'logo' }} color={colorsKeys[1]} />
                {data ? (
                    <React.Fragment>
                        {Array.isArray(data) &&
                            data.length > 0 &&
                            data.map((page, index) => (
                                <NavbarElement
                                    key={uuidv4()}
                                    page={page}
                                    color={colorsKeys[index % 4]}
                                    animate={animate}
                                />
                            ))}
                        <NavbarElement
                            page={{ name: 'search' }}
                            color={colorsKeys[(Array.isArray(data) ? data.length : 0) % 4]}
                            animate={animate}
                            navbarVisible={isNavbarVisible}
                        />
                    </React.Fragment>
                ) : (
                    ''
                )}
                <button className="anikino__navbar-burger" onClick={toggleMobileMenu}>
                    <MorphIcon icon={mobileMenuOpen ? X : Menu} spring="bouncy" />
                </button>
            </nav>
            {mobileMenuOpen && data && (
                <React.Fragment>
                    <div
                        className="anikino__mobile-menu-backdrop"
                        onClick={handleMobileMenuItemClick}
                        aria-hidden="true"
                    />
                    <NavbarMobileMenu
                        data={data}
                        colors={colorsKeys}
                        onItemClick={handleMobileMenuItemClick}
                    />
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export default Navbar;
